import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance, { publicInstance } from '../axiosInstance';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import BoxNumberSearch from './BoxNumberSearch';
import axios from 'axios';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTheme } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { format, parseISO } from 'date-fns';
import IconButton from '@mui/material/IconButton';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import Tooltip from '@mui/material/Tooltip';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CodeIcon from '@mui/icons-material/Code';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import RssiIndicator from '../../Components/RssiIndicator';
import posthog from 'posthog-js';
import ButtonGroup from '@mui/material/ButtonGroup';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';

function MapEventHandler({ onCameraChange }: { onCameraChange: (center: [number, number], zoom: number) => void }) {
    const map = useMapEvents({
        moveend: () => {
            const center = map.getCenter();
            onCameraChange([center.lat, center.lng], map.getZoom());
        },
        zoomend: () => {
            const center = map.getCenter();
            onCameraChange([center.lat, center.lng], map.getZoom());
        },
    });
    return null;
}

function createCustomIcon() {
    return new L.Icon({
        iconUrl: markerIcon,
        shadowUrl: markerShadow,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
    });
}

function InstalationDetailPage() {
    const theme = useTheme();
    const { boxNumber } = useParams();
    const navigate = useNavigate();
    const [responseData, setResponseData] = useState<Record<string, any> | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState<string>('Checking connection...');
    const [loadingConnection, setLoadingConnection] = useState<boolean>(true);
    const [serviceStatus, setServiceStatus] = useState<string>('');
    const [fetchStatus, setFetchStatus] = useState<string>('');
    const [dots, setDots] = useState<string>('');
    const [cancelSource, setCancelSource] = useState(axios.CancelToken.source());
    const [rebooting, setRebooting] = useState<boolean>(false);
    const [restartingService, setRestartingService] = useState<boolean>(false);
    const [gpsData, setGpsData] = useState<{ lat: number, lng: number, timestamp: string } | null>(null);
    const [mapInitialized, setMapInitialized] = useState(false);
    const [wifiConnectionStatus, setWifiConnectionStatus] = useState<string>('Checking WiFi connection...');
    const [wifiDots, setWifiDots] = useState<string>('');
    const [isScriptDialogOpen, setIsScriptDialogOpen] = useState(false);
    const [rssiData, setRssiData] = useState(null);
    const [loadingRssi, setLoadingRssi] = useState(false);
    const [rssiError, setRssiError] = useState(null);
    const [isWifiMode, setIsWifiMode] = useState(true);
    const abortControllerRef = useRef<AbortController | null>(null);
    const [isHelpOpen, setIsHelpOpen] = useState(false);


    const [cameraProps, setCameraProps] = useState({
        center: [0, 0] as [number, number],
        zoom: 17,
    });

    useEffect(() => {
        if (gpsData && !mapInitialized) {
            setCameraProps(prev => ({
                ...prev,
                center: [gpsData.lat, gpsData.lng]
            }));
            setMapInitialized(true);
        }
    }, [gpsData, mapInitialized]);

    const handleCameraChange = (newCenter: [number, number], newZoom: number) => {
        setCameraProps({
            center: newCenter,
            zoom: newZoom,
        });
    };

    const resetMapCamera = () => {
        if (gpsData) {
            setCameraProps({
                center: [gpsData.lat, gpsData.lng],
                zoom: 17,
            });
        }
    };

    const isDarkMode = theme.name === 'dark';

    const buttonStyle = {
        '&.MuiButton-root': {
            backgroundColor: theme.palette.primary.main,
            color: '#000000',
            '&:hover': {
                backgroundColor: theme.palette.primary.light,
            },
            fontSize: { xs: '0.7rem', sm: '0.875rem' },
            padding: { xs: '4px 8px', sm: '6px 16px' },
            whiteSpace: 'nowrap',
        },
    };

    const disabledButtonStyle = {
        '&.MuiButton-root': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: (theme.palette.text as any)?.disabled || '#9E9E9E',
            border: `1px solid ${theme.palette.borders.primary}`,
            opacity: 0.6,
            cursor: 'not-allowed',
            fontSize: { xs: '0.7rem', sm: '0.875rem' },
            padding: { xs: '4px 8px', sm: '6px 16px' },
            whiteSpace: 'nowrap',
        },
        '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: (theme.palette.text as any)?.disabled || '#9E9E9E',
        },
    };

    const customAlertStyle = {
        backgroundColor: isDarkMode ? '#333' : '#E0F7FA',  // Ciemne tło dla dark mode
        color: isDarkMode ? '#A8DADC' : '#00796B',  // Kolor tekstu
        border: isDarkMode ? '1px solid #457B9D' : '1px solid #004D40',  // Obwódka
        borderRadius: '8px',  // Zaokrąglenie rogów
        padding: '6px 16px',  // Adjust this value as needed
        marginTop: '-4px',    // Add a small negative margin if needed
    };

    const ranges = {
        "pm25": [0, 200],
        "pm10": [0, 200],
        "dust temp": [10, 50],
        "dust humid": [10, 80],
        "ambient temp": [-20, 40],
        "ambient press": [900, 1200],
        "ambient humid": [20, 90],
        "dust heater ntc": [10, 70],
        "dust rpm": [4450, 4550],
    };

    const getAdditionalDataColor = (key: string, value: string | undefined) => {
        if (typeof value !== 'string') {
            return 'black';
        }

        switch (key) {
            case 'network':
                return value.includes('OK') ? 'green' : 'red';
            case 'OSE_ESA':
                return value === 'OK' ? 'green' : 'red';
            case 'Displays MAC':
                return value !== 'N/A' ? 'green' : 'red';
            case 'SIM':
                if (value === 'ORANGE') return 'orange';
                if (value === 'PLUS') return 'green';
                if (value === 'PLAY') return 'violet';
                return 'red';
            case 'MODEM':
                return 'green';
            default:
                return 'black';
        }
    };

    useEffect(() => {
        const dotInterval = setInterval(() => {
            setDots(prevDots => (prevDots.length === 3 ? '' : prevDots + '.'));
        }, 500);

        return () => clearInterval(dotInterval);
    }, []);

    useEffect(() => {
        const wifiDotInterval = setInterval(() => {
            setWifiDots(prevDots => (prevDots.length === 3 ? '' : prevDots + '.'));
        }, 500);

        return () => clearInterval(wifiDotInterval);
    }, []);

    const checkWiFiConnection = useCallback(async (source: any) => {
        while (true) {
            try {
                const response = await axiosInstance.get(`/serwis/run-check-connection_wifi/${boxNumber}`, {
                    cancelToken: source.token,
                });
                if (response.data.status === "connection successful") {
                    setWifiConnectionStatus("WiFi Connection successful");
                    break;
                } else {
                    setWifiConnectionStatus("Checking WiFi connection...");
                }
            } catch (err) {
                if (axios.isCancel(err)) {
                    console.log('WiFi check canceled', err.message);
                    break;
                } else {
                    setWifiConnectionStatus("Checking WiFi connection...");
                }
            }
            await new Promise(resolve => setTimeout(resolve, 5000));
        }
    }, [boxNumber]);

    useEffect(() => {
        resetStates();
        const source = axios.CancelToken.source();
        setCancelSource(source);
        checkConnection(source);
        checkWiFiConnection(source);

        // Reset map-related states
        setGpsData(null);
        setMapInitialized(false);
        setCameraProps({
            center: [0, 0],
            zoom: 17,
        });

        // Clear RSSI data when navigating to a new box number
        setRssiData(null);
        setRssiError(null);

        if (boxNumber) {
            fetchGpsData(boxNumber);
        }

        return () => {
            source.cancel('Operation canceled due to box number change.');
        };
    }, [boxNumber, checkWiFiConnection]);

    useEffect(() => {
        delete (L.Icon.Default.prototype as any)._getIconUrl;
        L.Icon.Default.mergeOptions({
            iconRetinaUrl: markerIcon,
            iconUrl: markerIcon,
            shadowUrl: markerShadow,
        });
    }, []);

    const resetStates = () => {
        setResponseData(null);
        setLoading(false);
        setError(null);
        setConnectionStatus('Checking GSM connection...');
        setLoadingConnection(true);
        setServiceStatus('');
        setFetchStatus('');
        setWifiConnectionStatus('Checking WiFi connection...');
    };

    const checkConnection = async (source: any) => {
        try {
            const response = await axiosInstance.get(`/serwis/run-check-connection/${boxNumber}`, {
                cancelToken: source.token,
            });
            if (response.data.status === "connection successful") {
                setConnectionStatus("GSM Connection successful");
                setLoadingConnection(false);
                checkService(source);
            } else {
                throw new Error("GSM Connection lost");
            }
        } catch (err) {
            if (axios.isCancel(err)) {
                console.log('Request canceled', err.message);
            } else {
                setTimeout(() => checkConnection(source), 5000);
            }
        }
    };

    const checkService = async (source: any) => {
        setLoading(true);
        const intervalId = setInterval(async () => {
            try {
                const response = await axiosInstance.get(`/serwis/run-service-check/${boxNumber}`, {
                    cancelToken: source.token,
                });
                if (response.data.output === "Service up") {
                    setServiceStatus("Service running");
                    clearInterval(intervalId);
                    // Add a 5-second pause before fetching data
                    setTimeout(() => handleButtonClick(source), 5000);
                } else if (response.data.output === "Service down") {
                    setServiceStatus("Service not running");
                    console.log('Service not running, will retry...');
                }
            } catch (err) {
                if (axios.isCancel(err)) {
                    console.log('Request canceled', err.message);
                } else {
                    console.log('Error while checking service status, will retry...');
                }
            }
        }, 5000);

        return () => clearInterval(intervalId);
    };

    const handleButtonClick = async (source: any) => {
        setLoading(true);
        setError(null);
        setFetchStatus('Fetching data...');

        try {
            const response = await axiosInstance.get(`/serwis/run-macio-read-box/${boxNumber}`, {
                cancelToken: source.token,
            });
            setResponseData(response.data);
            setFetchStatus('Data fetched');
        } catch (err) {
            if (axios.isCancel(err)) {
                console.log('Request canceled', err.message);
            } else {
                setError('An error occurred while fetching data.');
                setFetchStatus('Failed to fetch data');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDeviceReboot = async () => {
        const startTime = new Date();
        posthog.capture('device_reboot_initiated', {
            username: localStorage.getItem('username'),
            box_number: boxNumber,
            timestamp: startTime.toISOString()
        });

        try {
            const response = await axiosInstance.get(`/serwis/run-box-reboot/${boxNumber}`);
            posthog.capture('device_reboot_completed', {
                username: localStorage.getItem('username'),
                box_number: boxNumber,
                success: response.data.output === "Reboot completed",
                duration_ms: new Date().getTime() - startTime.getTime(),
                timestamp: new Date().toISOString()
            });
        } catch (err) {
            posthog.capture('device_reboot_failed', {
                username: localStorage.getItem('username'),
                box_number: boxNumber,
                error: err.message,
                timestamp: new Date().toISOString()
            });
        }
    };

    const handleServiceRestart = async () => {
        setRestartingService(true);
        setServiceStatus(''); // Resetowanie statusu usługi
        setFetchStatus(''); // Resetowanie statusu pobierania
        try {
            const response = await axiosInstance.get(`/serwis/run-sv-restart/${boxNumber}`);
            if (response.data.output === "sv restart completed") {
                checkService(cancelSource);
            } else {
                setError("Service restart failed");
            }
        } catch (err) {
            setError("An error occurred during service restart.");
        } finally {
            setRestartingService(false);
        }
    };

    const handleRefresh = () => {
        setResponseData(null);
        setFetchStatus('');
        const newSource = axios.CancelToken.source();
        setCancelSource(newSource);
        handleButtonClick(newSource);
    };

    const extractMAC = (hardwareString: string | undefined): string => {
        if (!hardwareString) {
            return 'N/A';
        }
        const macMatch = hardwareString.match(/MAC\s([\dA-Fa-f:]+)/);
        return macMatch ? macMatch[1] : 'N/A';
    };

    const renderTable = (data: Record<string, any>, isMeasurement: boolean = false) => (
        <TableContainer component={Paper} sx={{ border: `1px solid ${theme.palette.borders.primary}`, borderRadius: 2 }}>
            <Table size="small">
                <TableBody>
                    {data && Object.entries(data).map(([key, value]) => {
                        const numericValue = parseFloat(value);
                        const [min, max] = ranges[key] || [null, null];
                        const isInRange = min !== null && max !== null && numericValue >= min && numericValue <= max;
                        const color = isMeasurement
                            ? isInRange ? 'green' : 'red'
                            : getAdditionalDataColor(key, value);

                        return (
                            <TableRow key={key}>
                                <TableCell>{key}</TableCell>
                                <TableCell sx={{ color }}>
                                    {String(value)}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const measurements = responseData ? {
        "pm25": responseData["pm25"],
        "pm10": responseData["pm10"],
        "dust temp": responseData["dust temp"],
        "dust humid": responseData["dust humid"],
        "ambient temp": responseData["ambient temp"],
        "ambient press": responseData["ambient press"],
        "ambient humid": responseData["ambient humid"],
        "dust heater ntc": responseData["dust heater ntc"],
        "dust rpm": responseData["dust rpm"]
    } : {};

    const additionalInfo = responseData ? {
        "network": responseData["network"],
        "OSE_ESA": responseData["OSE_ESA"],
        "Displays MAC": extractMAC(responseData["hardware"]),
        "SIM": responseData["SIM"],
        "MODEM": responseData["MODEM"]
    } : {};

    const isBoxOperational = () => {
        const measurementDataKeys = Object.keys(measurements);
        const areMeasurementsValid = measurementDataKeys.every((key) => {
            const numericValue = parseFloat(measurements[key]);
            const [min, max] = ranges[key] || [null, null];
            return min !== null && max !== null && numericValue >= min && numericValue <= max;
        });

        const additionalInfoKeys = Object.keys(additionalInfo);
        const isAdditionalInfoValid = additionalInfoKeys.every((key) => {
            const value = additionalInfo[key];
            const color = getAdditionalDataColor(key, value);
            return color !== 'red';
        });

        return areMeasurementsValid && isAdditionalInfoValid;
    };

    const fetchGpsData = async (boxNumber: string) => {
        try {
            const response = await publicInstance.get(`/dacapi_gps_current?box=eq.${boxNumber}`);
            if (response.data.length > 0) {
                const gpsString = response.data[0].gps;
                const gpsCoords = parseGps(gpsString);
                const timestamp = formatTimestamp(response.data[0].time_stamp);
                setGpsData({ ...gpsCoords, timestamp });
                setCameraProps(prev => ({
                    ...prev,
                    center: [gpsCoords.lat, gpsCoords.lng]
                }));
            }
        } catch (error) {
            console.error('Error fetching GPS data:', error);
        }
    };

    const formatTimestamp = (timestamp: string): string => {
        const date = parseISO(timestamp);
        return format(date, 'dd.MM.yyyy HH:mm:ss');
    };

    const parseGps = (gpsString: string): { lat: number, lng: number } => {
        const [latPart, lngPart] = gpsString.split(' ');
        const lat = parseFloat(latPart.slice(0, -1)) * (latPart.slice(-1) === 'N' ? 1 : -1);
        const lng = parseFloat(lngPart.slice(0, -1)) * (lngPart.slice(-1) === 'E' ? 1 : -1);
        return { lat, lng };
    };

    const generateGoogleMapsLink = (lat: number, lng: number) => {
        return `https://www.google.com/maps?q=${lat},${lng}`;
    };

    const handleScriptDialogOpen = () => {
        setIsScriptDialogOpen(true);
    };

    const handleCancelRssiCheck = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            setRssiData(null);
            setRssiError(null);
            setLoadingRssi(false);
        }
    }, []);

    const handleScriptDialogClose = useCallback(() => {
        if (loadingRssi) {
            handleCancelRssiCheck();
        }
        setIsScriptDialogOpen(false);
    }, [loadingRssi, handleCancelRssiCheck]);

    const handleModeToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (!loadingRssi) {
            setIsWifiMode(event.target.checked);
            // Clear RSSI data and error when mode is toggled
            setRssiData(null);
            setRssiError(null);
        }
    }, [loadingRssi]);

    const handleRssiCheck = useCallback(async () => {
        posthog.capture('rssi_check_initiated', {
            username: localStorage.getItem('username'),
            box_number: boxNumber,
            mode: isWifiMode ? 'wifi' : 'gsm',
            timestamp: new Date().toISOString()
        });
        setRssiData(null);
        setRssiError(null);
        setLoadingRssi(true);

        abortControllerRef.current = new AbortController();

        try {
            const endpoint = isWifiMode ?
                `/serwis/run-rssi-check-a51/${boxNumber}` :
                `/serwis/run-rssi-check-e30/${boxNumber}`;
            const response = await axiosInstance.get(endpoint, {
                signal: abortControllerRef.current.signal
            });

            if (response.data.error === "Nie udało się odczytać danych RSSI") {
                setRssiError("Failed to read RSSI data");
            } else {
                setRssiData(response.data);
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                setRssiError('');
            }
        } finally {
            setLoadingRssi(false);
        }
    }, [isWifiMode, boxNumber]);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            setRssiData(null);
            setRssiError(null);
            setLoadingRssi(false);
        };
    }, []);

    const handleHelpOpen = () => setIsHelpOpen(true);
    const handleHelpClose = () => setIsHelpOpen(false);

    return (
        <Box sx={{ p: 4, width: '100%', height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <BoxNumberSearch />

            <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                <Grid item xs={12} lg={7} order={{ xs: 1, lg: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                        <Typography
                            variant="h4"
                            component="h1"
                            sx={{
                                fontSize: { xs: '1.5rem', sm: '2.125rem' },
                                mb: { xs: 2, sm: 2 }
                            }}
                        >
                            Dashboard for Box: {boxNumber}
                        </Typography>

                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 1,
                            alignItems: { xs: 'stretch', sm: 'center' },
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}>
                                <ButtonGroup
                                    variant="contained"
                                    size="small"
                                    orientation="horizontal"
                                    sx={{
                                        display: 'flex',
                                        '& .MuiButton-root': {
                                            flex: 1,
                                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                        }
                                    }}
                                >
                                    <Button
                                        onClick={handleDeviceReboot}
                                        disabled={loadingConnection || rebooting}
                                        sx={loadingConnection || rebooting ? disabledButtonStyle : buttonStyle}
                                    >
                                        Reboot
                                    </Button>
                                    <Button
                                        onClick={handleServiceRestart}
                                        disabled={serviceStatus !== "Service running" || restartingService}
                                        sx={serviceStatus !== "Service running" || restartingService ? disabledButtonStyle : buttonStyle}
                                    >
                                        Restart
                                    </Button>
                                    <Button
                                        onClick={handleRefresh}
                                        disabled={fetchStatus !== 'Data fetched' || loading}
                                        sx={fetchStatus !== 'Data fetched' || loading ? disabledButtonStyle : buttonStyle}
                                    >
                                        Refresh
                                    </Button>
                                    <Button
                                        onClick={handleScriptDialogOpen}
                                        sx={buttonStyle}
                                    >
                                        Scripts
                                    </Button>
                                </ButtonGroup>
                                <IconButton
                                    onClick={handleHelpOpen}
                                    size="small"
                                    sx={{
                                        backgroundColor: theme.palette.action.hover,
                                        '&:hover': {
                                            backgroundColor: theme.palette.action.selected,
                                        },
                                    }}
                                >
                                    <HelpOutlineIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Card sx={{ border: `1px solid ${theme.palette.borders.primary}`, borderRadius: 2 }}>
                                <CardContent>
                                    <Typography variant="h5" component="div" gutterBottom>
                                        Status
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Box
                                                        sx={{
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: '50%',
                                                            backgroundColor: connectionStatus === "GSM Connection successful" ? 'green' : 'red',
                                                            marginRight: 2,
                                                        }}
                                                    />
                                                    <Typography>
                                                        {loadingConnection ? `Checking GSM connection${dots}` : connectionStatus}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Box
                                                        sx={{
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: '50%',
                                                            backgroundColor: wifiConnectionStatus === "WiFi Connection successful" ? 'green' : 'red',
                                                            marginRight: 2,
                                                        }}
                                                    />
                                                    <Typography>
                                                        {wifiConnectionStatus === 'Checking WiFi connection...'
                                                            ? `Checking WiFi connection${wifiDots}`
                                                            : wifiConnectionStatus}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        {connectionStatus === "GSM Connection successful" && (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Box
                                                    sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        backgroundColor: serviceStatus === "Service running" ? 'green' : 'red',
                                                        marginRight: 2,
                                                    }}
                                                />
                                                <Typography>
                                                    {serviceStatus === "" ? `Checking service${dots}` : serviceStatus}
                                                </Typography>
                                            </Box>
                                        )}
                                        {serviceStatus === "Service running" && (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Box
                                                    sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        backgroundColor: fetchStatus === 'Data fetched' ? 'green' : 'red',
                                                        marginRight: 2,
                                                    }}
                                                />
                                                <Typography>
                                                    {loading ? `Fetching data${dots}` : fetchStatus}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {fetchStatus === 'Data fetched' && (
                            isBoxOperational() ? (
                                <Grid item xs={12}>
                                    <Card sx={{ border: `1px solid ${theme.palette.borders.primary}`, borderRadius: 2 }}>
                                        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', height: '100px' }}>
                                            <CheckCircleIcon style={{ color: 'green', fontSize: 50 }} />
                                            <Typography variant="h5" component="div" color="green">
                                                Box is operational
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ) : (
                                <Grid item xs={12}>
                                    <Card sx={{ border: `1px solid ${theme.palette.borders.primary}`, borderRadius: 2 }}>
                                        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', height: 'auto', py: 3 }}>
                                            <CancelIcon style={{ color: 'red', fontSize: 50 }} />
                                            <Typography variant="h5" component="div" color="red" align="center">
                                                Box is not operational
                                            </Typography>
                                            {additionalInfo['Displays MAC'] === 'N/A' && (
                                                <Typography
                                                    variant="body1"
                                                    color="error"
                                                    sx={{ mt: 1, textAlign: 'center' }}
                                                >
                                                    No communication with display
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )
                        )}

                        {fetchStatus === 'Data fetched' && (
                            <>
                                <Grid item xs={12} lg={6}>
                                    <Card sx={{ border: `1px solid ${theme.palette.borders.primary}`, borderRadius: 2 }}>
                                        <CardContent>
                                            <Typography variant="h5" component="div" gutterBottom>
                                                Additional Data
                                            </Typography>
                                            {renderTable(additionalInfo)}
                                            {additionalInfo['Displays MAC'] === 'N/A' && (
                                                <Alert severity="info" sx={customAlertStyle}>
                                                    Często tablica włącza się dłużej niż usługa. Najlepiej poczekać, aż na tablicy pojawią się wartości i wtedy dokonać refresh danych.
                                                </Alert>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} lg={6}>
                                    <Card sx={{ border: `1px solid ${theme.palette.borders.primary}` }}>
                                        <CardContent>
                                            <Typography variant="h5" component="div" gutterBottom>
                                                Measurement Data
                                            </Typography>
                                            {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
                                            {responseData && renderTable(measurements, true)}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Grid>

                <Grid item xs={12} lg={5} order={{ xs: 2, lg: 2 }}>
                    {gpsData ? (
                        <Box sx={{ height: { xs: '600px', lg: '100%' }, width: '100%' }}>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} lg={6}>
                                    <Typography variant="body1">
                                        GPS: {gpsData.lat}, {gpsData.lng}
                                    </Typography>
                                    <Typography variant="body1">
                                        GPS Timestamp: {gpsData.timestamp}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="body1">
                                            <a
                                                href={generateGoogleMapsLink(gpsData.lat, gpsData.lng)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#1976D2', textDecoration: 'underline' }}
                                            >
                                                Open in Google Maps
                                            </a>
                                        </Typography>
                                        <Tooltip title="Reset map view to initial position" arrow>
                                            <IconButton
                                                onClick={resetMapCamera}
                                                size="small"
                                                sx={{ ml: 1 }}
                                            >
                                                <CenterFocusStrongIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <Alert severity="info" sx={customAlertStyle}>
                                        The map shows the last successfully read GPS data. GPS data is updated daily in the evening.
                                    </Alert>
                                </Grid>
                            </Grid>
                            <Box sx={{ height: { xs: 'calc(100% - 120px)', lg: 'calc(100% - 80px)' }, width: '100%' }}>
                                <MapContainer
                                    center={cameraProps.center}
                                    zoom={cameraProps.zoom}
                                    style={{ height: '100%', width: '100%' }}
                                >
                                    <TileLayer
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    />
                                    {gpsData && (
                                        <Marker position={[gpsData.lat, gpsData.lng]} icon={createCustomIcon()} />
                                    )}
                                    <MapEventHandler onCameraChange={handleCameraChange} />
                                </MapContainer>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ height: { xs: '400px', lg: '100%' }, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Typography variant="body1">No GPS data available for this box number.</Typography>
                        </Box>
                    )}
                </Grid>
            </Grid>

            {/* Script Dialog */}
            <Dialog
                open={isScriptDialogOpen}
                onClose={handleScriptDialogClose}
                aria-labelledby="script-dialog-title"
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: {
                        width: '95%',
                        height: '95%',
                        maxWidth: 'none',
                        maxHeight: 'none',
                        border: `1px solid ${theme.palette.borders.primary}`,
                    },
                }}
            >
                <DialogTitle id="script-dialog-title" sx={{ m: 0, p: 2 }}>
                    <IconButton
                        aria-label="close"
                        onClick={handleScriptDialogClose}
                        sx={{
                            position: 'absolute',
                            left: 10,
                            top: 10,
                            color: (theme) => theme.palette.grey[500],
                            '& .MuiSvgIcon-root': {
                                fontSize: '2rem',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography variant="h5" component="h1" sx={{ ml: 6, mt: 0.5 }}>Scripts for Box: {boxNumber}</Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isWifiMode}
                                onChange={handleModeToggle}
                                name="modeToggle"
                                color="primary"
                                disabled={loadingRssi}
                            />
                        }
                        label={isWifiMode ? "WiFi Mode" : "GSM Mode"}
                        sx={{ mb: 2 }}
                    />
                    <Typography variant="h6" gutterBottom>RSSI Check ({isWifiMode ? 'WiFi' : 'GSM'})</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {loadingRssi ? (
                            <>
                                <Button
                                    onClick={handleCancelRssiCheck}
                                    variant="contained"
                                    color="secondary"
                                    sx={buttonStyle}
                                >
                                    Cancel RSSI Check
                                </Button>
                                <CircularProgress size={24} />
                            </>
                        ) : (
                            <Button
                                onClick={handleRssiCheck}
                                variant="contained"
                                sx={buttonStyle}
                            >
                                Run RSSI Check ({isWifiMode ? 'WiFi' : 'GSM'})
                            </Button>
                        )}
                    </Box>
                    {rssiError && (
                        <Typography color="error" sx={{ mt: 2 }}>{rssiError}</Typography>
                    )}
                    {rssiData && (
                        <>
                            <RssiIndicator rssi={rssiData.rssi} />
                            <TextField
                                label="RSSI Data"
                                multiline
                                rows={8}
                                value={JSON.stringify(rssiData, null, 2)}
                                fullWidth
                                variant="outlined"
                                sx={{ mt: 2 }}
                                InputProps={{
                                    readOnly: true,
                                }}
                            />
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Dodaj dialog z legendą */}
            <Dialog
                open={isHelpOpen}
                onClose={handleHelpClose}
                aria-labelledby="help-dialog-title"
                PaperProps={{ sx: { border: `1px solid ${theme.palette.borders.primary}` } }}
            >
                <DialogTitle id="help-dialog-title">
                    Button Actions Legend
                </DialogTitle>
                <DialogContent>
                    <List>
                        <ListItem>
                            <ListItemIcon>
                                <RestartAltIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Reboot"
                                secondary="Restarts the entire device. Use this if the device is not responding properly."
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <RestartAltIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Restart"
                                secondary="Restarts only the service. Less invasive than a full reboot, use when only the service is not working."
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <RefreshIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Refresh"
                                secondary="Updates the displayed data with the latest information from the device."
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CodeIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Scripts"
                                secondary="Opens additional diagnostic tools and scripts that can be run on the device."
                            />
                        </ListItem>
                    </List>
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default InstalationDetailPage;